import {Loader, Transition} from '@mantine/core';
import {type FC, Fragment, useEffect, useState} from 'react';
import type {HashtagCategoryType} from '../../../../core/entities/entrysheetBuilder/constants';
import type {GetHashtagType} from '../../../../core/entities/register/schema';
import {FreeTextMessage} from './Presenter/FreeTextMessage';
import {HashtagSelect} from './Presenter/HashtagSelect';
import {SelectChoiceContent} from './Presenter/SelectChoiceContent';
import {SystemMessage} from './Presenter/SystemMessage';

type Props = {
  index: number;
  qaId: number;
  qaInputType: string;
  options: Array<{
    id: number;
    choice: string;
  }>;
  qaQuestion: string;
  selectedOptions: string[];
  setThemeId: (id: number) => void;
  handleSelectOption: (value: string, index?: number) => void;
  selectedHashtags: GetHashtagType[];
  setSelectedHashtags: (hashtags: GetHashtagType[]) => void;
  setHashtagCategory: (hashtag: HashtagCategoryType | undefined) => void;
  handleSelectHashtag: (index?: number) => void;
  cancel: (id: number) => void;
  updateEditingState: (index: number, isEditing: boolean) => void;
};

const isDev = process.env.NODE_ENV === 'development';

export const AiEsBuilderChat: FC<Props> = ({
  index,
  qaId,
  qaInputType,
  options,
  qaQuestion,
  selectedOptions,
  setThemeId,
  handleSelectOption,
  selectedHashtags,
  setSelectedHashtags,
  setHashtagCategory,
  handleSelectHashtag,
  cancel,
  updateEditingState,
}) => {
  const duration = 1000;
  const transition = 'fade';
  const [systemMounted, setSystemMounted] = useState(false);
  const [userMounted, setUserMounted] = useState(false);
  useEffect(() => {
    const timer = setTimeout(
      () => {
        setSystemMounted(true);
      },
      isDev ? 1 : 1000,
    );
    return () => {
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (systemMounted) {
      const timer = setTimeout(
        () => {
          setUserMounted(true);
        },
        isDev ? 1 : 1000,
      );
      return () => {
        clearTimeout(timer);
      };
    }
  }, [systemMounted]);

  return (
    <Fragment key={index}>
      {!systemMounted && (
        <div className="w-full flex justify-center mt-16">
          <Loader variant="dots" size="lg" className="fill-gradient-ai-l" />
        </div>
      )}
      <Transition
        mounted={systemMounted}
        transition={transition}
        duration={duration}
      >
        {(styles) => (
          <div className="mt-16" style={styles}>
            <SystemMessage message={qaQuestion} />
          </div>
        )}
      </Transition>
      <Transition
        mounted={userMounted}
        transition={transition}
        duration={duration}
      >
        {(styles) => (
          <div className="mt-16" style={styles}>
            {qaInputType === 'choice' && (
              <div key={qaId} className="flex justify-end">
                <SelectChoiceContent
                  index={index}
                  qaId={qaId}
                  options={options ?? []}
                  selectedOptions={selectedOptions}
                  setThemeId={setThemeId}
                  handleSelectOption={handleSelectOption}
                  updateEditingState={updateEditingState}
                />
              </div>
            )}
            {qaInputType === 'hashtag' && (
              <HashtagSelect
                key={index}
                index={index}
                setSelectedHashtags={setSelectedHashtags}
                selectedOptions={selectedOptions}
                selectedHashtags={selectedHashtags}
                setHashtagCategory={setHashtagCategory}
                handleSelectHashtag={handleSelectHashtag}
                cancel={cancel}
                updateEditingState={updateEditingState}
              />
            )}
            {qaInputType === 'free' && (
              <div key={index}>
                <FreeTextMessage
                  selectedOptions={selectedOptions}
                  index={index}
                  handleSelectOption={handleSelectOption}
                  updateEditingState={updateEditingState}
                />
              </div>
            )}
          </div>
        )}
      </Transition>
    </Fragment>
  );
};
